import { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, alpha, Tabs, Tab } from '@mui/material';
import type { TeamMember, CreateTeamMemberRequest, UpdateTeamMemberRequest } from '../services/timetablesService';
import { timetablesService } from '../services/timetablesService';
import { TeamMemberList } from '../components/timetables/TeamMemberList';
import { TeamMemberModal } from '../components/timetables/TeamMemberModal';
import { ScheduleEditor } from '../components/timetables/ScheduleEditor';
import { SettingsPanel } from '../components/timetables/SettingsPanel';
import { CoverageHeatmap } from '../components/timetables/CoverageHeatmap';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

export default function TimetablesPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'members' | 'settings'>('members');
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Modal states
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | undefined>();
  const [scheduleMember, setScheduleMember] = useState<TeamMember | undefined>();

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await timetablesService.getTeamMembers();
      setTeamMembers(data);
    } catch (err) {
      console.error('Failed to load team members:', err);
      setError('Failed to load team members. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async (data: CreateTeamMemberRequest) => {
    try {
      await timetablesService.createTeamMember(data);
      await loadTeamMembers();
      setShowMemberModal(false);
    } catch (err) {
      console.error('Failed to create team member:', err);
      alert('Failed to create team member. Please try again.');
    }
  };

  const handleUpdateMember = async (data: UpdateTeamMemberRequest) => {
    if (!editingMember) return;

    try {
      await timetablesService.updateTeamMember(editingMember.id, data);
      await loadTeamMembers();
      setShowMemberModal(false);
      setEditingMember(undefined);
    } catch (err) {
      console.error('Failed to update team member:', err);
      alert('Failed to update team member. Please try again.');
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;

    try {
      await timetablesService.deleteTeamMember(memberId);
      await loadTeamMembers();
    } catch (err) {
      console.error('Failed to delete team member:', err);
      alert('Failed to delete team member. Please try again.');
    }
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    setShowMemberModal(true);
  };

  const handleEditSchedule = (member: TeamMember) => {
    setScheduleMember(member);
    setShowScheduleEditor(true);
  };

  const handleScheduleSaved = async () => {
    await loadTeamMembers();
    setShowScheduleEditor(false);
    setScheduleMember(undefined);
  };

  const handleAddMember = () => {
    setEditingMember(undefined);
    setShowMemberModal(true);
  };

  if (loading) {
    return (
      <PageLayout>
        <PageHeader
          icon={<ScheduleIcon sx={{ fontSize: 28 }} />}
          title='Team Timetables'
          subtitle="Manage your team's schedules and availability"
        />
        <ContentSection>
          <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' minHeight='300px' gap={2}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant='h6' color='text.secondary'>
              Loading team timetables...
            </Typography>
          </Box>
        </ContentSection>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <PageHeader icon={<ScheduleIcon sx={{ fontSize: 28 }} />} title='Team Timetables' subtitle='Error loading team schedules' />
        <ContentSection>
          <Box textAlign='center' py={4}>
            <Alert
              severity='error'
              sx={{
                mb: 2,
                background: 'linear-gradient(135deg, rgba(227, 27, 35, 0.1), rgba(227, 27, 35, 0.05))',
                border: `1px solid ${alpha('#E31B23', 0.3)}`,
              }}
            >
              <Typography variant='h6' sx={{ mb: 1 }}>
                Failed to load team timetables
              </Typography>
              <Typography variant='body2'>{error}</Typography>
            </Alert>
            <Button variant='contained' color='error' onClick={loadTeamMembers}>
              Try Again
            </Button>
          </Box>
        </ContentSection>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        icon={<ScheduleIcon sx={{ fontSize: 28 }} />}
        title='Team Timetables'
        subtitle="Manage your team's schedules and availability"
        actions={
          activeTab === 'members' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={showHeatmap ? 'contained' : 'outlined'}
                onClick={() => setShowHeatmap(!showHeatmap)}
                startIcon={<ViewModuleIcon />}
              >
                {showHeatmap ? 'Hide' : 'Show'} Coverage Heatmap
              </Button>
              <Button variant='contained' onClick={handleAddMember} startIcon={<GroupIcon />}>
                Add Team Member
              </Button>
            </Box>
          )
        }
      />

      <ContentSection>
        <EnhancedCard variant='outlined' sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 64,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                backgroundColor: 'primary.main',
              },
            }}
          >
            <Tab value='members' label='Team Members' icon={<GroupIcon />} iconPosition='start' />
            <Tab value='settings' label='Discord Settings' icon={<SettingsIcon />} iconPosition='start' />
          </Tabs>
        </EnhancedCard>

        <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
          {activeTab === 'members' ? (
            <>
              <TeamMemberList
                teamMembers={teamMembers}
                onEdit={handleEditMember}
                onDelete={handleDeleteMember}
                onEditSchedule={handleEditSchedule}
              />
              {showHeatmap && <CoverageHeatmap teamMembers={teamMembers} />}
            </>
          ) : (
            <SettingsPanel />
          )}
        </Box>
      </ContentSection>

      {/* Modals */}
      <TeamMemberModal
        isOpen={showMemberModal}
        onClose={() => {
          setShowMemberModal(false);
          setEditingMember(undefined);
        }}
        onSave={
          editingMember
            ? data => handleUpdateMember(data as UpdateTeamMemberRequest)
            : data => handleCreateMember(data as CreateTeamMemberRequest)
        }
        member={editingMember}
      />

      {scheduleMember && (
        <ScheduleEditor
          member={scheduleMember}
          isOpen={showScheduleEditor}
          onClose={() => {
            setShowScheduleEditor(false);
            setScheduleMember(undefined);
          }}
          onSave={handleScheduleSaved}
        />
      )}
    </PageLayout>
  );
}
